<script lang="ts">
  import { type SanityContext, key } from '$lib/context/sanity';
  import type { Card } from '$lib/sanity/schemas';
  import { getContext } from 'svelte';
  import {
    toImageSrcAttributes,
    toLazyloadSrcsetUrlBuilders,
    toSrcsetUrlBuilders,
    useSanityImageLazyload
  } from '$lib/sanity/image';
  import { pipe } from '$lib/utils/pipe';
  import { toCSSVars } from '$lib/utils/cssVars';
  import { extractAspectRatioFromAsset } from '$lib/utils/sanity';
  import { accessibilityPreferences } from '$lib/stores/accessibility';
  import { ResponsiveVars, TwistGroup } from '@twist/core';
  import Circle from '$components/News/ButtonShapes/circle.svelte';
  import PortableText from '$components/SanityComponents/PortableText/PortableText.svelte';
  import FontSizeDecorator from '../PortableText/Decorators/FontSizeDecorator.svelte';
  import BlockButton from '$components/Button/BlockButton.svelte';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { cx } from '@emotion/css';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';

  interface Props {
    document: Card;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
  }

  let { document, sourceMap, path = [] }: Props = $props();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));

  const { isInView, lazyload } = useSanityImageLazyload();
  const { urlBuilder } = getContext<SanityContext>(key);

  let srcAttrs = $derived(
    document?.image?.asset &&
      pipe(
        document.image.asset,
        toSrcsetUrlBuilders(urlBuilder),
        toLazyloadSrcsetUrlBuilders($isInView),
        toImageSrcAttributes()
      )
  );

  let variantProperties = $derived(
    document.variant.type === 'vertical'
      ? [`${document.variant.topElement}TopElement`]
      : [`${document.variant.imagePosition}ImagePosition`, `${document.variant.alignment}Alignment`]
  );

  let aspectRatio = $derived(document.image && extractAspectRatioFromAsset(document.image));

  const responsiveVars = new ResponsiveVars()
    .addFluid('spacer', toFluidVarInput(24, 24))
    .addFluid('gap', toFluidVarInput(30, 60))
    .toCSS();
</script>

<TwistGroup>
  {#snippet children({ active: groupActive }: { active: boolean })}
    <svelte:element this={document.url ? 'a' : 'div'} href={document.url} class="card-root">
      <div class={cx(responsiveVars, 'card', document.variant.type, ...variantProperties)}>
        {#if srcAttrs}
          <img
            use:lazyload
            class="card-image"
            srcset={srcAttrs.srcset}
            src={srcAttrs.src}
            alt={document.image.altText}
            style={toCSSVars({ aspectRatio })}
            data-sanity={encodeDataAttribute([...path, 'image', 'asset', '_id'])}
          />
        {/if}
        <div class="card-content">
          {#if document.divider}
            <div class="card-divider" data-sanity={encodeDataAttribute([...path, 'divider'])}></div>
          {/if}
          {#if document.eyebrowText}
            <div class="card-eyebrow" data-sanity={encodeDataAttribute([...path, 'eyebrowText'])}>
              <FontSizeDecorator size="breadcrumb">
                {document.eyebrowText}
              </FontSizeDecorator>
            </div>
          {/if}
          <div class="card-headline">
            <PortableText {sourceMap} path={[...path, 'headline']} body={document.headline} />
          </div>
          <div class="card-summary">
            <PortableText {sourceMap} path={[...path, 'summary']} body={document.summary} />
          </div>
          {#if document.button?.text && document.url}
            <div class="card-button">
              <BlockButton
                motion={$accessibilityPreferences.reduceMotion ? 'never' : 'when-active'}
                href={document.url}
                {groupActive}
                data-sanity={encodeDataAttribute([...path, 'buttonText'])}
              >
                {document.button.text}
                {#snippet icon()}
                  <Circle />
                {/snippet}
              </BlockButton>
            </div>
          {/if}
        </div>
      </div>
    </svelte:element>
  {/snippet}
</TwistGroup>

<style lang="scss">
  @import 'src/scss/media';
  @import 'src/scss/accessibility';
  @import 'src/scss/media';
  @import 'src/scss/accessibility';
  @import 'src/scss/lazyload';
  @import 'src/scss/variables';

  a.card-root {
    cursor: pointer;

    &,
    &:focus,
    &:hover,
    &:visited {
      color: $text-default-color;
      text-decoration: none;
    }
  }

  @include without-reduce-motion {
    .card-root:focus {
      outline: none;
    }
  }

  .card {
    display: grid;
    text-align: left;

    .card-eyebrow,
    .card-headline,
    .card-summary,
    .card-image {
      margin-bottom: var(--spacer);
    }

    .card-divider {
      margin-bottom: var(--spacer);
    }

    &.vertical {
      &.headlineTopElement {
        grid-template-areas:
          'divider'
          'eyebrow'
          'headline'
          'image'
          'summary'
          'button';

        & > .card-content {
          display: contents;
        }
      }

      &.imageTopElement {
        grid-template-areas: 'image' 'content';

        .card-divider {
          margin-top: var(--spacer);
        }
      }

      &.no-image {
        .card-divider {
          margin-top: 0;
        }
      }
    }

    &.horizontal {
      // Render vertial alignment variant on mobile

      &.leftImagePosition {
        grid-template-areas: 'image' 'content';

        .card-divider {
          margin-top: var(--spacer);

          @include at('lg') {
            margin-top: 0;
          }
        }
      }

      &.rightImagePosition {
        grid-template-areas:
          'divider'
          'eyebrow'
          'headline'
          'image'
          'summary'
          'button';

        & > .card-content {
          display: contents;
        }
      }

      // Render horizontal variant on desktop
      @include at('lg') {
        grid-template-columns: repeat(2, 1fr);
        grid-column-gap: var(--gap);

        &.leftImagePosition {
          grid-template-areas: 'image content';
        }

        &.rightImagePosition {
          grid-template-areas: 'content image';

          & > .card-content {
            display: block;
          }
        }

        .card-divider {
          margin-top: 0;
        }
      }

      &.middleAlignment {
        .card-content,
        .card-image {
          margin: auto 0;
        }
      }

      &.bottomAlignment {
        .card-content,
        .card-image {
          margin: auto 0 0;
        }
      }
    }
  }

  .card-image {
    @include lazyload-blur;

    aspect-ratio: var(--aspect-ratio);
    width: 100%;
    grid-area: image;
    position: relative;
    border-radius: var(--theme-general-image-border-radius, none);
  }

  .card-content {
    grid-area: content;
    overflow: hidden;
    overflow-wrap: break-word;
  }

  .card-divider {
    display: block;
    width: 100%;
    grid-area: divider;
    border-top: $stroke-width-1 solid var(--theme-general-foreground-color, $border-depth-1-color);
  }

  .card-eyebrow {
    grid-area: eyebrow;
  }

  .card-headline {
    grid-area: headline;
  }

  .card-summary {
    grid-area: summary;
  }

  .card-button {
    grid-area: button;
  }
</style>
